import { energyLabels } from "@/db/definitions";
import { listingFilterSchema } from "@luntero/db/contracts/listing-filters";
import type { ListingFilters } from "@luntero/db/contracts/listing-filters";
export {
  type ListingFilters,
  listingFilterSchema,
  listingSortOptions,
} from "@luntero/db/contracts/listing-filters";
import { z } from "zod";

export const parseListingFilter = (data: unknown): ListingFilters => {
  const parsedFilters = listingFilterSchema.safeParse(data);

  if (!parsedFilters.success) {
    return parseListingFilter({});
  }

  const result = parsedFilters.data;

  if (!result.furnished) {
    result.furnished = "ANY";
  }
  if (!result.condition) {
    result.condition = "ANY";
  }
  if (!result.parkingType) {
    result.parkingType = "ANY";
  }
  if (!result.petsAllowed) {
    result.petsAllowed = "ANY";
  }

  result.type = normalizeMultiSelect(result.type);
  const minimumEnergyLabel = getMinimumEnergyLabel(result.energyLabel);
  result.energyLabel = minimumEnergyLabel ? [minimumEnergyLabel] : undefined;
  result.buildingFacilities = normalizeMultiSelect(result.buildingFacilities);

  return result;
};

const normalizeMultiSelect = <T extends string>(values: T[] | undefined) =>
  values ? Array.from(new Set(values)).sort() : undefined;

type SearchParamsReader = Pick<URLSearchParams, "getAll" | "keys">;

export const searchParamsToObject = (
  params: SearchParamsReader,
): { [key: string]: string | string[] } => {
  const result: { [key: string]: string | string[] } = {};

  for (const key of new Set(Array.from(params.keys()))) {
    const values = params.getAll(key);
    result[key] = values.length > 1 ? values : values[0];
  }

  return result;
};

export const getResetFilters = (): ListingFilters => {
  return parseListingFilter({});
};

export const getCountActiveFilters = (filters: ListingFilters): number => {
  const keys = Object.keys(filters) as (keyof ListingFilters)[];
  const nonCountableKeys: (keyof ListingFilters)[] = [
    "page",
    "pageSize",
    "sort",
  ];
  const hasAnyAsDefault = [
    "furnished",
    "condition",
    "parkingType",
    "petsAllowed",
  ];

  return keys
    .filter((key) => !nonCountableKeys.includes(key))
    .filter((key) => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (hasAnyAsDefault.includes(key)) {
        return value !== "ANY";
      }

      return Boolean(value);
    }).length;
};

export const getFiltersUrlQuery = (filters: ListingFilters) => {
  const params = new URLSearchParams();
  if (filters.search) {
    params.append("search", filters.search);
  }
  if (filters.page !== 1) {
    params.append("page", filters.page.toString());
  }
  if (filters.pageSize !== 24) {
    params.append("pageSize", filters.pageSize.toString());
  }
  if (filters.sort !== "recommended") {
    params.append("sort", filters.sort);
  }
  if (filters.type) {
    filters.type.forEach((type) => {
      params.append("type", type);
    });
  }
  if (filters.furnished && filters.furnished !== "ANY") {
    params.append("furnished", filters.furnished);
  }
  if (filters.condition && filters.condition !== "ANY") {
    params.append("condition", filters.condition);
  }
  if (filters.parkingType && filters.parkingType !== "ANY") {
    params.append("parkingType", filters.parkingType);
  }
  if (filters.petsAllowed && filters.petsAllowed !== "ANY") {
    params.append("petsAllowed", filters.petsAllowed);
  }
  if (filters.priceMin) {
    params.append("priceMin", filters.priceMin.toString());
  }
  if (filters.priceMax) {
    params.append("priceMax", filters.priceMax.toString());
  }
  if (filters.areaMin) {
    params.append("areaMin", filters.areaMin.toString());
  }
  if (filters.areaMax) {
    params.append("areaMax", filters.areaMax.toString());
  }
  if (filters.volumeMin) {
    params.append("volumeMin", filters.volumeMin.toString());
  }
  if (filters.volumeMax) {
    params.append("volumeMax", filters.volumeMax.toString());
  }
  if (filters.bathroomsMin) {
    params.append("bathroomsMin", filters.bathroomsMin.toString());
  }
  if (filters.bathroomsMax) {
    params.append("bathroomsMax", filters.bathroomsMax.toString());
  }
  if (filters.floorsMin) {
    params.append("floorsMin", filters.floorsMin.toString());
  }
  if (filters.floorsMax) {
    params.append("floorsMax", filters.floorsMax.toString());
  }
  if (filters.gardenAreaMin) {
    params.append("gardenAreaMin", filters.gardenAreaMin.toString());
  }
  if (filters.gardenAreaMax) {
    params.append("gardenAreaMax", filters.gardenAreaMax.toString());
  }
  if (filters.yearMin) {
    params.append("yearMin", filters.yearMin.toString());
  }
  if (filters.yearMax) {
    params.append("yearMax", filters.yearMax.toString());
  }
  if (filters.bedroomsMin) {
    params.append("bedroomsMin", filters.bedroomsMin.toString());
  }
  if (filters.bedroomsMax) {
    params.append("bedroomsMax", filters.bedroomsMax.toString());
  }
  if (filters.ownerId) params.append("ownerId", filters.ownerId);
  if (filters.cityId) params.append("cityId", filters.cityId);
  if (filters.city && filters.city !== "ANY") {
    params.append("city", filters.city);
  }
  if (filters.province && filters.province !== "ANY") {
    params.append("province", filters.province);
  }
  if (filters.borough && filters.borough !== "ANY") {
    params.append("borough", filters.borough);
  }
  if (filters.neighborhood && filters.neighborhood !== "ANY") {
    params.append("neighborhood", filters.neighborhood);
  }
  if (filters.street && filters.street !== "ANY") {
    params.append("street", filters.street);
  }
  if (filters.atLeastOneImage) {
    params.append("atLeastOneImage", "true");
  }
  if (filters.hasBalcony !== undefined) {
    params.append("hasBalcony", filters.hasBalcony.toString());
  }
  if (filters.hasGarden !== undefined) {
    params.append("hasGarden", filters.hasGarden.toString());
  }
  if (filters.tenantType && filters.tenantType !== "ANY") {
    params.append("tenantType", filters.tenantType);
  }
  if (filters.energyLabel) {
    filters.energyLabel.forEach((label) => {
      params.append("energyLabel", label);
    });
  }
  if (filters.buildingFacilities) {
    filters.buildingFacilities.forEach((facility) => {
      params.append("buildingFacilities", facility);
    });
  }
  if (filters.floorNumber) {
    params.append("floorNumber", filters.floorNumber.toString());
  }
  if (filters.isAvailableNow !== undefined) {
    params.append("isAvailableNow", filters.isAvailableNow.toString());
  }
  if (filters.createdAtMin) {
    params.append("createdAtMin", filters.createdAtMin.toISOString());
  }

  const query: { [key: string]: string | string[] } = {};
  for (const key of params.keys()) {
    const values = params.getAll(key);
    query[key] = values.length > 1 ? values : values[0];
  }

  return { query, params };
};

export const listingHistogramFilterSchema = listingFilterSchema.pick({
  type: true,
  furnished: true,
  city: true,
  province: true,
  atLeastOneImage: true,
  energyLabel: true,
  hasBalcony: true,
  hasGarden: true,
});

export type ListingHistogramFilters = z.infer<
  typeof listingHistogramFilterSchema
>;

export const parseListingHistogramFilter = (
  data: unknown,
): ListingHistogramFilters => {
  const parsed = listingHistogramFilterSchema.safeParse(data);

  if (!parsed.success) {
    return {
      type: undefined,
      furnished: "ANY",
      city: undefined,
      province: undefined,
    };
  }

  const r = parsed.data;

  return {
    type: r.type && r.type.length > 0 ? r.type : undefined,
    furnished: r.furnished ?? "ANY",
    city: r.city || undefined,
    province: r.province || undefined,
    atLeastOneImage: r.atLeastOneImage || undefined,
    energyLabel: normalizeMultiSelect(r.energyLabel),
    hasBalcony: r.hasBalcony,
    hasGarden: r.hasGarden,
  };
};

export const getHistogramFiltersUrlQuery = (
  filters: ListingHistogramFilters,
) => {
  const params = new URLSearchParams();

  if (filters.type && filters.type.length > 0) {
    [...filters.type].sort().forEach((t) => params.append("type", t));
  }

  if (filters.furnished && filters.furnished !== "ANY") {
    params.append("furnished", filters.furnished);
  }

  if (filters.city && filters.city !== "ANY") {
    params.append("city", filters.city);
  }
  if (filters.province && filters.province !== "ANY") {
    params.append("province", filters.province);
  }

  if (filters.atLeastOneImage) params.append("atLeastOneImage", "true");
  filters.energyLabel
    ?.slice()
    .sort()
    .forEach((label) => params.append("energyLabel", label));
  if (filters.hasBalcony !== undefined)
    params.append("hasBalcony", String(filters.hasBalcony));
  if (filters.hasGarden !== undefined)
    params.append("hasGarden", String(filters.hasGarden));

  const query: { [key: string]: string | string[] } = {};
  for (const key of params.keys()) {
    const values = params.getAll(key);
    query[key] = values.length > 1 ? values : values[0];
  }

  return { query, params };
};

/** Ratings are ordered best first; the least efficient requested label is the minimum. */
export const getMinimumEnergyLabel = (labels: string[] | undefined) =>
  energyLabels.filter((label) => labels?.includes(label)).at(-1);

export const getQualifyingEnergyLabels = (labels: string[] | undefined) => {
  const minimum = getMinimumEnergyLabel(labels);
  return minimum
    ? energyLabels.slice(0, energyLabels.indexOf(minimum) + 1)
    : [];
};
